import { apiRequest, apiUpload } from "./apiClient";

const EDITOR_MAX_DIMENSION = 1800;
const BITMAP_RESIZE_WIDTH = 1440;

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
  canvas.toBlob((webp) => {
    if (webp) return resolve(webp);
    canvas.toBlob(
      (jpeg) => jpeg ? resolve(jpeg) : reject(new Error("PROFILE_PHOTO_ENCODE_FAILED")),
      "image/jpeg",
      0.9
    );
  }, "image/webp", 0.9);
});

const drawBoundedImage = async (image, width, height) => {
  const scale = Math.min(1, EDITOR_MAX_DIMENSION / Math.max(width, height));
  const outputWidth = Math.max(1, Math.round(width * scale));
  const outputHeight = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("PROFILE_PHOTO_CANVAS_UNAVAILABLE");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, outputWidth, outputHeight);
  context.drawImage(image, 0, 0, outputWidth, outputHeight);
  const blob = await canvasToBlob(canvas);
  canvas.width = 1;
  canvas.height = 1;
  return blob;
};

const prepareWithImageElement = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = "async";
  const release = () => {
    image.onload = null;
    image.onerror = null;
    image.src = "";
    URL.revokeObjectURL(url);
  };
  image.onload = async () => {
    try {
      const result = await drawBoundedImage(image, image.naturalWidth, image.naturalHeight);
      release();
      resolve(result);
    } catch (error) {
      release();
      reject(error);
    }
  };
  image.onerror = () => {
    release();
    reject(new Error("PROFILE_PHOTO_DECODE_FAILED"));
  };
  image.src = url;
});

/**
 * iOS WKWebView'da çok yüksek çözünürlüklü galeri görsellerini editörde ham
 * haliyle tutmak süreç belleğini hızla artırabilir. Editör açılmadan önce tek
 * seferde, EXIF yönünü tarayıcıya uygulatıp sınırlı bir önizleme blob'u üretir.
 */
export async function profilFotografiniEditorIcinHazirla(file) {
  if (!file) throw new Error("PROFILE_PHOTO_REQUIRED");
  try {
    if (typeof createImageBitmap === "function") {
      let bitmap;
      try {
        try {
          bitmap = await createImageBitmap(file, {
            imageOrientation: "from-image",
            resizeWidth: BITMAP_RESIZE_WIDTH,
            resizeQuality: "high",
          });
        } catch (_) {
          // Eski iOS sürümleri resize seçeneklerini desteklemeyebilir.
          bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
        }
        try {
          return await drawBoundedImage(bitmap, bitmap.width, bitmap.height);
        } finally {
          bitmap?.close?.();
        }
      } catch (_) {
        // Bazı eski WKWebView sürümleri createImageBitmap'i sunmasına rağmen
        // HEIC/JPEG çözümlemesinde hata verir. Image elementi daha geniş uyumludur.
        bitmap?.close?.();
      }
    }
    return await prepareWithImageElement(file);
  } catch (error) {
    const normalized = new Error("PROFILE_PHOTO_PREPARE_FAILED");
    normalized.code = "PROFILE_PHOTO_PREPARE_FAILED";
    normalized.cause = error;
    throw normalized;
  }
}

export async function profilFotografiYukleService(file, onProgress) {
  const formData = new FormData();
  formData.append("photo", file, "profile-photo.webp");
  return apiUpload("/auth/profile-picture", formData, { onProgress });
}

export async function profilFotografiSilService(fallbackAvatar = null) {
  return apiRequest("/auth/profile-picture", {
    method: "DELETE",
    body: JSON.stringify({ fallbackAvatar }),
  });
}
